import { openSplitViewState } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { FC, memo, useCallback, useMemo } from 'react';
import { activeAttachmentIdState, ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import EmailToSupplier from '../../../components/EmailToSupplier/EmailToSupplier';
import { purchaseOrderLabels, salesInvoiceLabels } from './EmailToCounterpartySection.constants';
import { EmailToCounterpartySectionProps } from './EmailToCounterpartySection.types';

export const EmailToCounterpartySection: FC<EmailToCounterpartySectionProps> = memo((props) => {
    const { request } = props;

    const { details } = request;
    const isSalesInvoice = details.integrationCode === domain.IntegrationCode.QBooksInvoice;

    const setOpenSplitView = useSetAtom(openSplitViewState);
    const setContentSplitView = useSetAtom(contentSplitViewState);
    const activeAttachmentId = useAtomValue(activeAttachmentIdState);

    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));

    const onAttachmentOpen = useCallback(
        (attachment: domain.RequestAttachment) => {
            setOpenSplitView(true);
            setContentSplitView({
                attachmentId: attachment.id,
                mode: ContentSplitViewMode.AttachmentsToEmailToSupplier,
            });
        },
        [setContentSplitView, setOpenSplitView]
    );

    const supplier = isSalesInvoice ? details.customer : details.vendor;

    const contactPerson = useMemo(
        () => supplier?.contactPersons?.find(({ firstName }) => Boolean(firstName)) || null,
        [supplier?.contactPersons]
    );

    if (!isSalesInvoice && (!details.emailToSupplier || !details.sendToSupplier)) {
        return null;
    }

    const replacements = {
        [domain.SupplierEmailReplacements.poNumber]: details.number,
        [domain.SupplierEmailReplacements.invoiceNumber]: details.number,
        [domain.SupplierEmailReplacements.totalAmount]: intl.formatCurrency(request.amount, request.currency),
        [domain.SupplierEmailReplacements.supplierName]: supplier?.text || null,
        [domain.SupplierEmailReplacements.customerName]: supplier?.text || null,
        [domain.SupplierEmailReplacements.companyName]: company?.displayName || null,
        [domain.SupplierEmailReplacements.contactPersonName]: contactPerson?.firstName || null,
    };

    return (
        <EmailToSupplier
            request={request}
            labels={isSalesInvoice ? salesInvoiceLabels : purchaseOrderLabels}
            placeholderReplacements={replacements}
            onAttachmentOpen={onAttachmentOpen}
            activeAttachmentId={activeAttachmentId}
        />
    );
});

EmailToCounterpartySection.displayName = 'EmailToCounterpartySection';
